import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { BookmarkSimpleIcon, TrashIcon, ArrowRightIcon } from "@phosphor-icons/react";

export default function Highlights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/highlights")
      .then((r) => setItems(r.data || []))
      .catch((err) => captureError("highlights load failed:", err))
      .finally(() => setLoading(false));
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/highlights/${id}`);
      setItems((prev) => prev.filter((h) => h.id !== id));
    } catch (err) { captureError("delete highlight failed:", err); }
  };

  return (
    <AppLayout>
      <div className="mb-6 fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FDB927] mb-2 inline-flex items-center gap-1.5">
          <BookmarkSimpleIcon weight="fill" /> Saved
        </p>
        <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Your Highlights</h1>
        <p className="text-sm text-white/55 mt-2">Pitches and plays you've bookmarked across games.</p>
      </div>

      {loading ? (
        <div className="text-white/60 text-sm" data-testid="highlights-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-8 rounded-md bg-[#121212] border border-white/10 text-center" data-testid="highlights-empty">
          <BookmarkSimpleIcon size={28} className="mx-auto mb-2 text-white/30" />
          <p className="text-white/60 text-sm mb-4">No highlights yet. Hit “Save” on any pitch or play to bookmark it.</p>
          <Link to="/games" className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider">
            Browse games <ArrowRightIcon size={13} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="highlights-grid">
          {items.map((h) => (
            <div key={h.id} data-testid={`highlight-${h.id}`} className="p-4 rounded-md bg-[#121212] border border-white/10 flex items-start justify-between gap-3">
              <Link to={`/games/${h.game_id}`} className="min-w-0 group">
                <span className="text-[9px] font-black uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#0A84FF]/15 text-[#0A84FF] border border-[#0A84FF]/40">{h.kind}</span>
                <p className="font-display font-bold text-base uppercase tracking-tight mt-1.5 group-hover:text-[#0A84FF] transition-colors">{h.title}</p>
                {h.detail && <p className="text-xs text-white/60 mt-0.5">{h.detail}</p>}
              </Link>
              <button onClick={() => remove(h.id)} data-testid={`highlight-delete-${h.id}`} className="text-white/35 hover:text-[#FF453A] shrink-0"><TrashIcon size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
